import React, {useCallback} from 'react';
import dynamic from 'next/dynamic';
import get from 'lodash/get';
import getConfig from 'next/config';

import Article from '~/src/components/article/Article';
import Rating from '~/src/components/rating/Rating';
import ReportDialog from '~/src/components/report/ReportDialog';
import ActivityBar from '~/src/components/activity/bar/ActivityBar';
import StructuredArticle from '~/src/components/structured/article/StructuredArticle';

import {getContentUrl} from '~/src/utilities/helpers/link';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {ActionBubbleFormat} from '~/src/utilities/constants/formats';
import TypeContext from '~/src/components/context/TypeContext';
import {onRating} from '~/src/utilities/helpers/rating';

import css from './ArticleContent.module.scss';

const ActionBubble = dynamic({
    loader: () => import('~/src/components/actionBubble/ActionBubble'),
    loading: () => false,
    ssr: false
});

const {publicRuntimeConfig: config} = getConfig();

const ArticleContent = ({type, content, commenting, id, teasers}) => {
    const canonicalUrl = new URL(getContentUrl(content.meta.url), config.canonicalBaseUrl).href;

    const {titleHeader, title} = teasers.web;
    const headline = titleHeader ? `${titleHeader}: ${title}` : title;

    const datePublished = get(content, 'meta.published', content.created);
    const dateModified = get(content, 'meta.updated', content.updated);

    const onRatingAction = useCallback(
        onRating({
            id,
            title: content.article.title,
            authors: content.meta.authors,
            fullUrlPath: content.meta.mainCategoryFullUrlPath,
            type
        }),
        [id]
    );

    const activityBarData = get(content, 'meta.clientdata.community');

    if (isEnabled(Feature.TOTAL_COMMENTS) && commenting) {
        const totalCount = get(commenting, 'totalCount');
        if (totalCount) {
            activityBarData.comments = totalCount.toString();
        }
    }

    return (
        <TypeContext value={type}>
            <StructuredArticle
                datePublished={datePublished}
                dateModified={dateModified}
                authors={content.meta.authors}
                headline={headline}
                description={teasers.web.description}
                images={teasers.web.images}
                canonicalUrl={canonicalUrl}
            />

            <Article {...content} id={id} />

            {get(content, 'meta.settings.likesEnabled', true) && <Rating metadataId={id} onRating={onRatingAction} />}

            <ActivityBar data={activityBarData} settings={get(content, 'meta.settings')} metadataId={id} />

            <ReportDialog />

            <div className={css.actionBubbleContainer}>
                <div className={css.actionBubble}>
                    <ActionBubble metadataId={id} format={ActionBubbleFormat.ACTION_BUBBLE_ARTICLE} settings={get(content, 'meta.settings')} />
                </div>
            </div>
        </TypeContext>
    );
};

export default ArticleContent;
